import React, { useEffect, useState } from "react";
import { requirePurchaseAccount } from "../../Services/PurchaseAccess";
import {
	Button,
	CircularProgress,
	TextField,
	Typography,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import requests from "../../Services/HttpServices";
import PaymentModal from "../PaymentModal/PaymentModal";

const formatPrice = (value) => {
	const parsed = parseFloat(value || 0);
	return `${(Number.isNaN(parsed) ? 0 : parsed).toFixed(2).replace(".", ",")}€`;
};

const getLoggedUser = () => {
	try {
		return sessionStorage.getItem("User") && JSON.parse(sessionStorage.getItem("User")).userdata;
	} catch (e) {
		return null;
	}
};

export default function FuneralGiftsHomePopup({
	deceasedID,
	deceasedName,
	close,
}) {
	const [gifts, setGifts]       = useState([]);
	const [loading, setLoading]   = useState(true);
	const [error, setError]       = useState("");
	const [selected, setSelected] = useState(null);
	const [orderId, setOrderId]   = useState(null);
	const [creating, setCreating] = useState(false);
	const [completed, setCompleted] = useState(false);
	const [formError, setFormError] = useState("");
	const [dedica, setDedica]     = useState("");
	const [firma, setFirma]       = useState("");
	const [firmaError, setFirmaError] = useState(false);
	const loggedUser = getLoggedUser() || {};

	useEffect(() => {
		let mounted = true;
		setLoading(true);
		requests("get", "funeralGiftPublic", deceasedID)
			.then((result) => {
				if (!mounted) return;
				if (result?.data?.response === "success") {
					setGifts(result.data.data?.gifts || []);
					setError("");
				} else {
					setGifts([]);
					setError(result?.data?.message || "Doni Funerari non disponibili");
				}
				setLoading(false);
			})
			.catch(() => {
				if (!mounted) return;
				setGifts([]);
				setError("Doni Funerari non disponibili");
				setLoading(false);
			});
		return () => {
			mounted = false;
		};
	}, [deceasedID]);

	const handleClose = () => {
		if (orderId && !completed) {
			requests("post", "cancelFuneralGift", { orderid: orderId });
		}
		close();
	};

	const handleCompletedClose = () => {
		setCompleted(true);
		close();
	};

	const submitOrder = () => {
		if (!selected) return;
		if (!requirePurchaseAccount()) {
			return;
		}
		const trimmedFirma = firma.trim();
		const trimmedDedica = dedica.trim();
		if (!trimmedFirma) {
			setFirmaError(true);
			setFormError("La firma e' obbligatoria.");
			return;
		}
		setFirmaError(false);

		setCreating(true);
		setFormError("");
		requests("post", "funeralGiftOrder", {
			deceasedID,
			idDono : selected.idDono,
			dedica : trimmedDedica,
			firma  : trimmedFirma,
		}).then((response) => {
			if (response?.response === "success") {
				setOrderId(response.data?.orderid);
			} else {
				setFormError(response?.message || "Errore durante la creazione dell'ordine.");
			}
			setCreating(false);
		}).catch(() => {
			setFormError("Errore durante la creazione dell'ordine.");
			setCreating(false);
		});
	};

	return (
		<div className="funeral-gifts-modal-bg" onClick={handleClose}>
			<div className="funeral-gifts-modal" onClick={(event) => event.stopPropagation()}>
				<button type="button" className="funeral-gifts-close" onClick={handleClose}>
					<Close />
				</button>
				<Typography className="funeral-gifts-title">Doni Funerari</Typography>
				<Typography className="funeral-gifts-subtitle">{deceasedName}</Typography>

				{loading && (
					<div className="funeral-gifts-loader">
						<CircularProgress />
					</div>
				)}

				{!loading && error && <Typography className="funeral-gifts-error">{error}</Typography>}

				{!loading && !error && !orderId && (
					<>
						<div className="funeral-gifts-public-list">
							{gifts.map((gift) => (
								<button
									type="button"
									key={gift.idDono}
									className={`funeral-gift-public-card ${
										selected?.idDono === gift.idDono ? "selected" : ""
									}`}
									onClick={() => {
										setSelected(gift);
										setFormError("");
										setFirmaError(false);
									}}
								>
									{gift.imageUrl ? (
										<img src={gift.imageUrl} alt={gift.nome} />
									) : (
										<div className="funeral-gift-public-placeholder" />
									)}
									<span>{gift.nome}</span>
									<strong>{formatPrice(gift.prezzo)}</strong>
								</button>
							))}
						</div>

						{selected && (
							<div className="funeral-gift-message-fields">
								<TextField
									label="Firma"
									value={firma}
									onChange={(event) => {
										setFirma(event.target.value);
										if (event.target.value.trim()) setFirmaError(false);
									}}
									required
									error={firmaError}
									fullWidth
									variant="outlined"
									inputProps={{ maxLength: 255 }}
									helperText="Campo obbligatorio"
								/>
								<TextField
									label="Dedica"
									value={dedica}
									onChange={(event) => setDedica(event.target.value)}
									fullWidth
									multiline
									rows={3}
									variant="outlined"
									inputProps={{ maxLength: 2000 }}
									helperText="Opzionale"
								/>
							</div>
						)}

						{gifts.length === 0 && (
							<Typography className="funeral-gifts-empty">Nessun dono disponibile.</Typography>
						)}

						{formError && <Typography className="funeral-gifts-error">{formError}</Typography>}

						<div className="funeral-gifts-actions">
							<Button onClick={handleClose}>Annulla</Button>
							<Button
								variant="contained"
								disabled={!selected || creating}
								onClick={submitOrder}
								style={{ backgroundColor: "#fa0", color: "#111", fontWeight: "bold" }}
							>
								{creating ? "Attendi..." : "Acquista"}
							</Button>
						</div>
					</>
				)}

				{orderId && selected && (
					<PaymentModal
						paymentType="funeral-gift"
						price={selected.prezzo}
						orderId={orderId}
						quantity={1}
						purchase={selected.nome}
						showAddress={false}
						userData={{
							name    : loggedUser.name,
							surname : loggedUser.surname,
							CF      : loggedUser.cf,
							email   : loggedUser.email,
						}}
						onSuccess={() => setCompleted(true)}
						successComponent={() => (
							<div className="funeral-gifts-success">
								<Typography className="funeral-gifts-title">Pagamento completato</Typography>
								<Typography>Grazie, il dono funerario e' stato acquistato correttamente.</Typography>
								<Button
									variant="contained"
									onClick={handleCompletedClose}
									style={{ marginTop: 20, backgroundColor: "#fa0", color: "#111" }}
								>
									Chiudi
								</Button>
							</div>
						)}
					/>
				)}
			</div>
		</div>
	);
}
